/**
 * Portfolio quality / resilience model for wheel + margin books.
 * Pure functions — no DOM. Inputs come from own-ledger books + open options + cash.
 */

export type PfolioSeverity = "mild" | "moderate" | "severe" | "extreme";

export const PFOLIO_SEVERITIES: Array<{
  id: PfolioSeverity;
  label: string;
  equityDrop: number;
  topNameDrop: number;
  putAssignPct: number;
  buybackMult: number;
  marginRateMult: number;
}> = [
  { id: "mild", label: "Mild", equityDrop: 0.1, topNameDrop: 0.2, putAssignPct: 0.25, buybackMult: 1.5, marginRateMult: 1.25 },
  { id: "moderate", label: "Moderate", equityDrop: 0.2, topNameDrop: 0.35, putAssignPct: 0.5, buybackMult: 2, marginRateMult: 1.75 },
  { id: "severe", label: "Severe", equityDrop: 0.35, topNameDrop: 0.5, putAssignPct: 0.75, buybackMult: 3, marginRateMult: 2.5 },
  { id: "extreme", label: "Extreme", equityDrop: 0.5, topNameDrop: 0.7, putAssignPct: 1, buybackMult: 5, marginRateMult: 4 },
];

export interface PfolioHolding {
  ticker: string;
  accountId: string;
  quantity: number;
  avgCost: number;
  costBasis: number;
  price: number | null;
  marketValue: number | null;
}

export interface PfolioOption {
  ticker: string;
  side: "put" | "call";
  strike: number;
  contracts: number;
  expDate: string;
  dte: number | null;
  openCredit: number;
  entryPrice: number;
  stockPrice: number | null;
  accountId?: string | null;
}

export interface PfolioInputs {
  holdings: PfolioHolding[];
  options: PfolioOption[];
  /** Spendable cash equivalent (unleveraged BP / positive cash). */
  cashEquivalent: number | null;
  /** Robinhood overnight buying power when known. */
  buyingPower: number | null;
  /** Broker cash balance (may be negative on margin). */
  brokerCash: number | null;
  /** Own capital EOM when Analytics ledger is available. */
  ownCapital: number | null;
  /** Monthly margin interest estimate (APR/12 on used margin, or RH forecast). */
  monthlyMarginCost: number | null;
  marginApr?: number;
  asOf: string;
}

export interface PfolioMetric {
  id: string;
  label: string;
  value: string;
  detail: string;
  tone: "good" | "watch" | "risk" | "neutral";
  tip: string;
}

export interface PfolioConcentrationRow {
  ticker: string;
  costBasis: number;
  marketValue: number | null;
  sharePct: number;
  quantity: number;
}

export interface PfolioScenarioImpact {
  id: string;
  name: string;
  summary: string;
  /** Ordered consequence lines. */
  consequences: string[];
  tone: "good" | "watch" | "risk" | "neutral";
  /** Rough $ figures for the cards. */
  figures: Array<{ label: string; value: string; tone?: "good" | "watch" | "risk" | "neutral" }>;
}

export interface PfolioSnapshot {
  asOf: string;
  qualityScore: number | null;
  qualityBand: "resilient" | "adequate" | "fragile" | "unknown";
  /** One-line book read. */
  summary: string;
  /** Top action suggestion. */
  recommendation: string;
  metrics: PfolioMetric[];
  concentration: PfolioConcentrationRow[];
  book: {
    stockCostBasis: number;
    stockMarketValue: number;
    stockOpenPl: number;
    ownCapital: number | null;
    marginUsed: number;
    marginUtilizationPct: number | null;
    cashEquivalent: number | null;
    buyingPower: number | null;
    brokerCash: number | null;
    putCollateral: number;
    callSharesCovered: number;
    shareQuantity: number;
    shortPutContracts: number;
    shortCallContracts: number;
    monthlyMarginCost: number | null;
    runwayMonths: number | null;
  };
  dteBuckets: Array<{ label: string; puts: number; calls: number; putNotional: number }>;
  scenarios: PfolioScenarioImpact[];
  footnotes: string[];
}

/** Compact persisted day row (API + compare strip). */
export interface PfolioDayRow {
  date: string;
  asOf: string;
  qualityScore: number | null;
  qualityBand: string;
  marginUtilizationPct: number | null;
  marginUsed: number;
  stockCostBasis: number;
  cashEquivalent: number | null;
  putCollateral: number;
  collateralCoveragePct: number | null;
  topTicker: string | null;
  topTickerPct: number | null;
  nearPutNotional: number;
  coveredCallPct: number | null;
  runwayMonths: number | null;
  shortPutContracts: number;
  shortCallContracts: number;
  summary: string;
  recommendation: string;
  source: "auto" | "client";
}

const MARGIN_APR_DEFAULT = 0.045;

function clamp(n: number, lo: number, hi: number): number {
  return Math.max(lo, Math.min(hi, n));
}

function money(n: number): string {
  const sign = n < 0 ? "-" : "";
  return `${sign}$${Math.round(Math.abs(n)).toLocaleString("en-US")}`;
}

function pct(n: number | null, digits = 0): string {
  if (n == null || !Number.isFinite(n)) return "—";
  return `${n.toLocaleString("en-US", {
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  })}%`;
}

function toneFromThresholds(
  value: number,
  goodBelow: number,
  watchBelow: number,
): "good" | "watch" | "risk" {
  if (value < goodBelow) return "good";
  if (value < watchBelow) return "watch";
  return "risk";
}

function optionCollateral(opt: PfolioOption): number {
  return Math.max(0, opt.strike) * 100 * Math.max(0, opt.contracts);
}

function remainingCredit(opt: PfolioOption): number {
  // Prefer openCredit (total $); fall back to entry × 100 × contracts.
  if (Number.isFinite(opt.openCredit) && opt.openCredit > 0) return opt.openCredit;
  return Math.max(0, opt.entryPrice) * 100 * Math.max(0, opt.contracts);
}

function dteBucket(dte: number | null): string {
  if (dte == null || !Number.isFinite(dte)) return "Unknown";
  if (dte <= 7) return "≤7 DTE";
  if (dte <= 21) return "8–21 DTE";
  if (dte <= 45) return "22–45 DTE";
  return ">45 DTE";
}

/**
 * Build the Pfolio snapshot for one severity level.
 */
export function buildPfolioSnapshot(
  inputs: PfolioInputs,
  severity: PfolioSeverity = "moderate",
): PfolioSnapshot {
  const sev =
    PFOLIO_SEVERITIES.find((row) => row.id === severity) ?? PFOLIO_SEVERITIES[1];
  const apr = inputs.marginApr ?? MARGIN_APR_DEFAULT;

  const stockCostBasis = inputs.holdings.reduce((s, h) => s + h.costBasis, 0);
  let pricedBasis = 0;
  let stockMarketValue = 0;
  for (const h of inputs.holdings) {
    if (h.marketValue != null && h.price != null && h.price > 0) {
      stockMarketValue += h.marketValue;
      pricedBasis += h.costBasis;
    }
  }
  const stockOpenPl = stockMarketValue - pricedBasis;
  const shareQuantity = inputs.holdings.reduce((s, h) => s + h.quantity, 0);

  const puts = inputs.options.filter((o) => o.side === "put");
  const calls = inputs.options.filter((o) => o.side === "call");
  const putCollateral = puts.reduce((s, o) => s + optionCollateral(o), 0);
  const shortPutContracts = puts.reduce((s, o) => s + o.contracts, 0);
  const shortCallContracts = calls.reduce((s, o) => s + o.contracts, 0);
  const callSharesCovered = calls.reduce((s, o) => s + o.contracts * 100, 0);

  const ownCapital =
    inputs.ownCapital != null && Number.isFinite(inputs.ownCapital)
      ? inputs.ownCapital
      : null;

  // Margin used: prefer cost basis − own capital (Analytics rule); else |negative broker cash|.
  let marginUsed = 0;
  if (ownCapital != null) {
    marginUsed = Math.max(0, stockCostBasis - ownCapital);
  } else if (inputs.brokerCash != null && inputs.brokerCash < 0) {
    marginUsed = Math.abs(inputs.brokerCash);
  }

  const marginUtilizationPct =
    stockCostBasis > 0 ? (marginUsed / stockCostBasis) * 100 : null;

  const monthlyMarginCost =
    inputs.monthlyMarginCost != null && Number.isFinite(inputs.monthlyMarginCost)
      ? Math.max(0, inputs.monthlyMarginCost)
      : marginUsed > 0
        ? marginUsed * (apr / 12)
        : null;

  const cashEquivalent = inputs.cashEquivalent;
  const runwayMonths =
    cashEquivalent != null &&
    cashEquivalent > 0 &&
    monthlyMarginCost != null &&
    monthlyMarginCost > 0
      ? cashEquivalent / monthlyMarginCost
      : cashEquivalent != null && cashEquivalent > 0 && (monthlyMarginCost === 0 || monthlyMarginCost == null)
        ? null
        : cashEquivalent != null && cashEquivalent <= 0
          ? 0
          : null;

  const coveredPct =
    shareQuantity > 0 ? clamp((callSharesCovered / shareQuantity) * 100, 0, 100) : null;

  // Concentration by ticker (sum across accounts).
  const byTicker = new Map<string, PfolioConcentrationRow>();
  for (const h of inputs.holdings) {
    const prev = byTicker.get(h.ticker) ?? {
      ticker: h.ticker,
      costBasis: 0,
      marketValue: 0,
      sharePct: 0,
      quantity: 0,
    };
    prev.costBasis += h.costBasis;
    prev.quantity += h.quantity;
    if (h.marketValue != null) {
      prev.marketValue = (prev.marketValue ?? 0) + h.marketValue;
    }
    byTicker.set(h.ticker, prev);
  }
  const concentration = [...byTicker.values()]
    .map((row) => ({
      ...row,
      marketValue: row.marketValue && row.marketValue > 0 ? row.marketValue : null,
      sharePct: stockCostBasis > 0 ? (row.costBasis / stockCostBasis) * 100 : 0,
    }))
    .sort((a, b) => b.costBasis - a.costBasis);
  const top1 = concentration[0]?.sharePct ?? 0;
  const top3 = concentration.slice(0, 3).reduce((s, r) => s + r.sharePct, 0);

  const bucketOrder = ["≤7 DTE", "8–21 DTE", "22–45 DTE", ">45 DTE", "Unknown"];
  const bucketMap = new Map(
    bucketOrder.map((label) => [label, { label, puts: 0, calls: 0, putNotional: 0 }]),
  );
  for (const opt of inputs.options) {
    const key = dteBucket(opt.dte);
    const b = bucketMap.get(key)!;
    if (opt.side === "put") {
      b.puts += opt.contracts;
      b.putNotional += optionCollateral(opt);
    } else {
      b.calls += opt.contracts;
    }
  }
  const dteBuckets = bucketOrder
    .map((label) => bucketMap.get(label)!)
    .filter((b) => b.puts > 0 || b.calls > 0);

  // Near-term put pressure.
  const nearPutNotional = puts
    .filter((o) => o.dte != null && o.dte <= 21)
    .reduce((s, o) => s + optionCollateral(o), 0);

  const collateralCoverage =
    cashEquivalent != null && putCollateral > 0
      ? (cashEquivalent / putCollateral) * 100
      : cashEquivalent != null && putCollateral === 0
        ? 100
        : null;

  const metrics: PfolioMetric[] = [
    {
      id: "margin-util",
      label: "Margin utilization",
      value: pct(marginUtilizationPct),
      detail: `${money(marginUsed)} used on ${money(stockCostBasis)} stock basis`,
      tone:
        marginUtilizationPct == null
          ? "neutral"
          : toneFromThresholds(marginUtilizationPct, 35, 60),
      tip: "Margin used ÷ stock cost basis. Wheel books that lean on margin get fragile when marks fall.",
    },
    {
      id: "cash-buffer",
      label: "Cash vs CSP collateral",
      value: pct(collateralCoverage),
      detail:
        cashEquivalent == null
          ? "Cash equivalent unavailable"
          : `${money(cashEquivalent)} cash-eq · ${money(putCollateral)} short-put collateral`,
      tone:
        collateralCoverage == null
          ? "neutral"
          : collateralCoverage >= 100
            ? "good"
            : collateralCoverage >= 50
              ? "watch"
              : "risk",
      tip: "Spendable cash equivalent versus strike collateral on open short puts. Below 100% means assignment would force more margin.",
    },
    {
      id: "concentration",
      label: "Top ticker weight",
      value: pct(top1),
      detail: concentration[0]
        ? `${concentration[0].ticker} · top 3 ${pct(top3)}`
        : "No stock lots",
      tone: top1 <= 20 ? "good" : top1 <= 35 ? "watch" : "risk",
      tip: "Share of stock cost basis in the largest ticker. Wheel inventory that piles into one name turns a single gap into a book event.",
    },
    {
      id: "near-puts",
      label: "Puts ≤21 DTE",
      value: money(nearPutNotional),
      detail: `${shortPutContracts} put contracts open · ${money(putCollateral)} total CSP`,
      tone:
        putCollateral <= 0
          ? "good"
          : nearPutNotional / Math.max(putCollateral, 1) < 0.35
            ? "good"
            : nearPutNotional / Math.max(putCollateral, 1) < 0.6
              ? "watch"
              : "risk",
      tip: "Strike collateral on short puts with 21 or fewer days left. Assignment risk clusters here.",
    },
    {
      id: "cc-cover",
      label: "Covered-call cover",
      value: coveredPct == null ? "—" : pct(coveredPct),
      detail: `${callSharesCovered.toLocaleString("en-US")} sh covered / ${shareQuantity.toLocaleString("en-US")} held`,
      tone:
        coveredPct == null
          ? "neutral"
          : coveredPct >= 50
            ? "good"
            : coveredPct >= 20
              ? "watch"
              : "risk",
      tip: "Shares already spoken for by short calls. High cover locks upside; low cover leaves more naked long beta.",
    },
    {
      id: "runway",
      label: "Margin runway",
      value:
        runwayMonths == null
          ? monthlyMarginCost && monthlyMarginCost > 0 && (cashEquivalent ?? 0) <= 0
            ? "0 mo"
            : "—"
          : `${runwayMonths.toFixed(runwayMonths >= 10 ? 0 : 1)} mo`,
      detail:
        monthlyMarginCost == null
          ? "No margin cost estimate"
          : `${money(monthlyMarginCost)} / month interest est.`,
      tone:
        runwayMonths == null
          ? monthlyMarginCost && monthlyMarginCost > 0 && (cashEquivalent ?? 0) <= 0
            ? "risk"
            : "neutral"
          : runwayMonths >= 12
            ? "good"
            : runwayMonths >= 4
              ? "watch"
              : "risk",
      tip: "Cash equivalent ÷ estimated monthly margin interest. Thin runway means a quiet market still bleeds capital.",
    },
  ];

  // Quality score 0–100 (higher = more resilient).
  const scoreParts: number[] = [];
  if (marginUtilizationPct != null) {
    scoreParts.push(clamp(100 - marginUtilizationPct * 1.2, 0, 100));
  }
  if (collateralCoverage != null) {
    scoreParts.push(clamp(collateralCoverage, 0, 100));
  }
  scoreParts.push(clamp(100 - top1 * 1.5, 0, 100));
  if (putCollateral > 0) {
    scoreParts.push(
      clamp(100 - (nearPutNotional / putCollateral) * 100, 0, 100),
    );
  } else {
    scoreParts.push(85);
  }
  if (runwayMonths != null) {
    scoreParts.push(clamp(runwayMonths * 8, 0, 100));
  }
  const qualityScore = scoreParts.length
    ? Math.round(scoreParts.reduce((a, b) => a + b, 0) / scoreParts.length)
    : null;
  const qualityBand: PfolioSnapshot["qualityBand"] =
    qualityScore == null
      ? "unknown"
      : qualityScore >= 70
        ? "resilient"
        : qualityScore >= 50
          ? "adequate"
          : "fragile";

  const { summary, recommendation } = buildPfolioGuidance({
    qualityScore,
    qualityBand,
    marginUtilizationPct,
    collateralCoverage,
    topTicker: concentration[0]?.ticker ?? null,
    topTickerPct: top1,
    nearPutNotional,
    putCollateral,
    coveredCallPct: coveredPct,
    runwayMonths,
  });

  const priceByTicker = new Map<string, number>();
  for (const h of inputs.holdings) {
    if (h.price != null && h.price > 0) priceByTicker.set(h.ticker, h.price);
  }
  for (const o of inputs.options) {
    if (o.stockPrice != null && o.stockPrice > 0 && !priceByTicker.has(o.ticker)) {
      priceByTicker.set(o.ticker, o.stockPrice);
    }
  }

  const scenarios = buildScenarios({
    sev,
    stockCostBasis,
    stockMarketValue,
    stockOpenPl,
    ownCapital,
    marginUsed,
    cashEquivalent,
    putCollateral,
    puts,
    calls,
    concentration,
    priceByTicker,
    monthlyMarginCost,
    apr,
  });

  return {
    asOf: inputs.asOf,
    qualityScore,
    qualityBand,
    summary,
    recommendation,
    metrics,
    concentration: concentration.slice(0, 8),
    book: {
      stockCostBasis,
      stockMarketValue,
      stockOpenPl,
      ownCapital,
      marginUsed,
      marginUtilizationPct,
      cashEquivalent,
      buyingPower: inputs.buyingPower,
      brokerCash: inputs.brokerCash,
      putCollateral,
      callSharesCovered,
      shareQuantity,
      shortPutContracts,
      shortCallContracts,
      monthlyMarginCost,
      runwayMonths,
    },
    dteBuckets,
    scenarios,
    footnotes: [
      "Stress figures are approximate book maths, not broker margin-call quotes.",
      "Assignment uses strike collateral (wheel rule); option premiums stay in the open month.",
      "Cash equivalent is spendable capacity (unleveraged BP / positive cash), not overnight 2× BP.",
      `Margin interest default ${(apr * 100).toFixed(1)}% APR ÷ 12 when Robinhood daily interest is unavailable.`,
      "Daily metric history is saved automatically after Robinhood snapshot refresh (and when you open Pfolio).",
    ],
  };
}

export function buildPfolioGuidance(m: {
  qualityScore: number | null;
  qualityBand: PfolioSnapshot["qualityBand"] | string;
  marginUtilizationPct: number | null;
  collateralCoverage: number | null;
  topTicker: string | null;
  topTickerPct: number;
  nearPutNotional: number;
  putCollateral: number;
  coveredCallPct: number | null;
  runwayMonths: number | null;
}): { summary: string; recommendation: string } {
  const band =
    m.qualityBand === "resilient"
      ? "Resilient"
      : m.qualityBand === "adequate"
        ? "Adequate"
        : m.qualityBand === "fragile"
          ? "Fragile"
          : "Unknown";
  const scoreText = m.qualityScore != null ? `${m.qualityScore}/100` : "n/a";

  const pressures: string[] = [];
  if (m.collateralCoverage != null && m.collateralCoverage < 50) {
    pressures.push(
      `cash-eq covers only ${Math.round(m.collateralCoverage)}% of short-put CSP`,
    );
  }
  if (m.putCollateral > 0 && m.nearPutNotional / m.putCollateral >= 0.6) {
    pressures.push("most put collateral sits inside 21 DTE");
  }
  if (m.marginUtilizationPct != null && m.marginUtilizationPct >= 60) {
    pressures.push(`margin utilization is elevated at ${Math.round(m.marginUtilizationPct)}%`);
  }
  if (m.topTickerPct >= 35 && m.topTicker) {
    pressures.push(`${m.topTicker} is ${Math.round(m.topTickerPct)}% of stock basis`);
  }
  if (m.runwayMonths != null && m.runwayMonths < 4) {
    pressures.push(`margin runway is thin (~${m.runwayMonths.toFixed(1)} mo)`);
  }
  if (m.coveredCallPct != null && m.coveredCallPct < 20) {
    pressures.push("little of the share book is covered by short calls");
  }

  const summary = pressures.length
    ? `Book reads ${band} (${scoreText}): ${pressures.slice(0, 2).join("; ")}.`
    : `Book reads ${band} (${scoreText}) with no single metric flashing as the main stress point.`;

  let recommendation =
    "Keep sizing short puts to cash-eq and watch near-dated collateral.";
  if (m.collateralCoverage != null && m.collateralCoverage < 50) {
    recommendation =
      "Priority: raise cash-eq or trim near-dated short puts until CSP is closer to fully cash-backed.";
  } else if (m.putCollateral > 0 && m.nearPutNotional / m.putCollateral >= 0.6) {
    recommendation =
      "Priority: roll or close the ≤21 DTE put stack so assignment risk is not clustered this month.";
  } else if (m.marginUtilizationPct != null && m.marginUtilizationPct >= 60) {
    recommendation =
      "Priority: reduce stock basis or add capital so a mark drop does not push utilization further.";
  } else if (m.topTickerPct >= 35 && m.topTicker) {
    recommendation = `Priority: diversify away from ${m.topTicker} or hedge that name — one gap dominates the book.`;
  } else if (m.runwayMonths != null && m.runwayMonths < 4) {
    recommendation =
      "Priority: cut margin used or add cash — interest alone can force de-risking soon.";
  } else if (m.qualityBand === "resilient") {
    recommendation =
      "Maintain: keep CSP inside cash-eq and avoid stacking more near-dated short puts into one name.";
  } else if (m.qualityBand === "adequate") {
    recommendation =
      "Maintain with a bias to add cash buffer before opening new short puts.";
  }

  return { summary, recommendation };
}

/** Compact day row for history API from a live snapshot. */
export function dayRowFromSnapshot(snapshot: PfolioSnapshot): PfolioDayRow {
  const coverPct =
    snapshot.book.cashEquivalent != null && snapshot.book.putCollateral > 0
      ? (snapshot.book.cashEquivalent / snapshot.book.putCollateral) * 100
      : snapshot.book.cashEquivalent != null && snapshot.book.putCollateral === 0
        ? 100
        : null;
  const nearMetric = snapshot.metrics.find((m) => m.id === "near-puts");
  let nearPutNotional = 0;
  if (nearMetric) {
    const parsed = Number(nearMetric.value.replace(/[^0-9.-]/g, ""));
    if (Number.isFinite(parsed)) nearPutNotional = parsed;
  }
  const coveredCallPct =
    snapshot.book.shareQuantity > 0
      ? clamp((snapshot.book.callSharesCovered / snapshot.book.shareQuantity) * 100, 0, 100)
      : null;
  const top = snapshot.concentration[0];
  return {
    date: "",
    asOf: snapshot.asOf,
    qualityScore: snapshot.qualityScore,
    qualityBand: snapshot.qualityBand,
    marginUtilizationPct: snapshot.book.marginUtilizationPct,
    marginUsed: snapshot.book.marginUsed,
    stockCostBasis: snapshot.book.stockCostBasis,
    cashEquivalent: snapshot.book.cashEquivalent,
    putCollateral: snapshot.book.putCollateral,
    collateralCoveragePct: coverPct,
    topTicker: top?.ticker ?? null,
    topTickerPct: top?.sharePct ?? null,
    nearPutNotional,
    coveredCallPct,
    runwayMonths: snapshot.book.runwayMonths,
    shortPutContracts: snapshot.book.shortPutContracts,
    shortCallContracts: snapshot.book.shortCallContracts,
    summary: snapshot.summary,
    recommendation: snapshot.recommendation,
    source: "client",
  };
}

function buildScenarios(ctx: {
  sev: (typeof PFOLIO_SEVERITIES)[number];
  stockCostBasis: number;
  stockMarketValue: number;
  stockOpenPl: number;
  ownCapital: number | null;
  marginUsed: number;
  cashEquivalent: number | null;
  putCollateral: number;
  puts: PfolioOption[];
  calls: PfolioOption[];
  concentration: PfolioConcentrationRow[];
  priceByTicker: Map<string, number>;
  monthlyMarginCost: number | null;
  apr: number;
}): PfolioScenarioImpact[] {
  const {
    sev,
    stockMarketValue,
    marginUsed,
    cashEquivalent,
    putCollateral,
    puts,
    concentration,
    priceByTicker,
    monthlyMarginCost,
    apr,
    ownCapital,
    stockCostBasis,
  } = ctx;

  const drop = sev.equityDrop;
  const mtmLoss = stockMarketValue > 0 ? stockMarketValue * drop : stockCostBasis * drop;
  const marginAfter = marginUsed + mtmLoss;
  const cashAfterShock =
    cashEquivalent == null ? null : cashEquivalent - mtmLoss * 0.15; // partial BP drain heuristic
  const newlyItm: Array<{ ticker: string; contracts: number; notional: number }> = [];
  let newlyItmNotional = 0;
  for (const put of puts) {
    const px = priceByTicker.get(put.ticker);
    if (px == null || !(px > 0)) continue;
    const shocked = px * (1 - drop);
    const wasItm = px < put.strike;
    const nowItm = shocked < put.strike;
    if (nowItm && !wasItm) {
      const notional = optionCollateral(put);
      newlyItm.push({ ticker: put.ticker, contracts: put.contracts, notional });
      newlyItmNotional += notional;
    }
  }
  const equityTone: PfolioScenarioImpact["tone"] =
    mtmLoss > Math.max(marginUsed, 1) * 0.5 || (cashAfterShock != null && cashAfterShock < 0)
      ? "risk"
      : newlyItmNotional > putCollateral * 0.25
        ? "watch"
        : "good";

  const equityScenario: PfolioScenarioImpact = {
    id: "equity-drop",
    name: `Broad equity −${Math.round(drop * 100)}%`,
    summary: `Marks on the stock book fall about ${money(mtmLoss)}. Short puts that were OTM can flip ITM.`,
    tone: equityTone,
    figures: [
      { label: "Stock MTM", value: `−${money(mtmLoss)}`, tone: "risk" },
      { label: "Margin used after", value: money(marginAfter), tone: equityTone },
      {
        label: "New ITM put CSP",
        value: newlyItmNotional > 0 ? money(newlyItmNotional) : "$0",
        tone: newlyItmNotional > 0 ? "watch" : "good",
      },
      {
        label: "Cash-eq after (est.)",
        value: cashAfterShock == null ? "—" : money(cashAfterShock),
        tone:
          cashAfterShock == null
            ? "neutral"
            : cashAfterShock > 0
              ? "watch"
              : "risk",
      },
    ],
    consequences: [
      newlyItm.length
        ? `${newlyItm.length} put name(s) newly ITM (~${money(newlyItmNotional)} strike collateral): ${newlyItm
            .slice(0, 4)
            .map((r) => `${r.ticker} ${r.contracts}×`)
            .join(", ")}${newlyItm.length > 4 ? "…" : ""}`
        : "No additional short puts flip ITM at this drop (from known underlyings).",
      ownCapital != null
        ? `Own capital cushion vs basis shrinks; utilization moves toward ${pct(
            stockCostBasis > 0 ? (marginAfter / stockCostBasis) * 100 : null,
          )}.`
        : `Estimated margin used rises by roughly the mark loss (${money(mtmLoss)}).`,
      cashAfterShock != null && cashAfterShock < newlyItmNotional
        ? "Assignment of the newly ITM puts would likely require more margin — cash-eq does not cover that CSP."
        : "Cash-eq still covers the newly ITM put collateral in this sketch.",
    ],
  };

  const assignPct = sev.putAssignPct;
  const assignNotional = putCollateral * assignPct;
  const assignShares = Math.round(assignNotional / Math.max(1, avgPutStrike(puts) || 1)) * 100;
  const cashAfterAssign =
    cashEquivalent == null ? null : cashEquivalent - assignNotional;
  const assignTone: PfolioScenarioImpact["tone"] =
    cashAfterAssign != null && cashAfterAssign < 0
      ? "risk"
      : assignPct >= 0.75
        ? "watch"
        : "neutral";
  const assignScenario: PfolioScenarioImpact = {
    id: "put-assign-wave",
    name: `Put assignment wave (${Math.round(assignPct * 100)}%)`,
    summary: `Assume ${Math.round(assignPct * 100)}% of short-put strike collateral converts into shares at strike.`,
    tone: assignTone,
    figures: [
      { label: "Capital to fund", value: money(assignNotional), tone: assignTone },
      {
        label: "Cash-eq left",
        value: cashAfterAssign == null ? "—" : money(cashAfterAssign),
        tone:
          cashAfterAssign == null
            ? "neutral"
            : cashAfterAssign >= 0
              ? "watch"
              : "risk",
      },
      {
        label: "CSP remaining",
        value: money(putCollateral - assignNotional),
        tone: "neutral",
      },
    ],
    consequences: [
      `~${money(assignNotional)} leaves cash/BP and becomes long stock at the put strikes (wheel inventory).`,
      cashAfterAssign != null && cashAfterAssign < 0
        ? `Shortfall ${money(Math.abs(cashAfterAssign))} — broker margin would have to fund the rest.`
        : "Spendable cash-eq covers this assignment wave in the sketch.",
      "Open put count and near-term assignment pressure drop; stock concentration and margin basis rise.",
      assignShares > 0
        ? `Rough share intake on the order of ${assignShares.toLocaleString("en-US")} shares across assigned names (strike-weighted).`
        : "No open short puts to assign.",
    ],
  };

  const buybackBase = puts.concat(ctx.calls).reduce((s, o) => s + remainingCredit(o), 0);
  const buybackCost = buybackBase * sev.buybackMult;
  const cashAfterBuyback =
    cashEquivalent == null ? null : cashEquivalent - buybackCost;
  const buybackTone: PfolioScenarioImpact["tone"] =
    buybackCost > (cashEquivalent ?? 0)
      ? "risk"
      : buybackCost > (cashEquivalent ?? 0) * 0.4
        ? "watch"
        : "good";
  const buybackScenario: PfolioScenarioImpact = {
    id: "buyback-squeeze",
    name: `Buyback squeeze (×${sev.buybackMult} credit)`,
    summary: `Cost to flatten shorts if buybacks run about ${sev.buybackMult}× remaining open credit.`,
    tone: buybackTone,
    figures: [
      { label: "Est. BTC bill", value: money(buybackCost), tone: buybackTone },
      {
        label: "Open credit base",
        value: money(buybackBase),
        tone: "neutral",
      },
      {
        label: "Cash-eq after",
        value: cashAfterBuyback == null ? "—" : money(cashAfterBuyback),
        tone:
          cashAfterBuyback == null
            ? "neutral"
            : cashAfterBuyback >= 0
              ? "watch"
              : "risk",
      },
    ],
    consequences: [
      "Closing to avoid assignment or gamma burns premium already booked in earlier months — this cash hit is new.",
      cashAfterBuyback != null && cashAfterBuyback < 0
        ? "Cash-eq cannot fund a full flatten; some risk would have to be held or rolled."
        : "Cash-eq can fund this flatten sketch without new deposits.",
      "Wheel books that stack many near-dated shorts feel this first.",
    ],
  };

  const top = concentration[0];
  const topMv =
    top?.marketValue && top.marketValue > 0
      ? top.marketValue
      : top
        ? top.costBasis
        : 0;
  const topLoss = topMv * sev.topNameDrop;
  const topTone: PfolioScenarioImpact["tone"] =
    !top
      ? "neutral"
      : top.sharePct >= 35 || topLoss > marginUsed * 0.4
        ? "risk"
        : top.sharePct >= 20
          ? "watch"
          : "good";
  const topScenario: PfolioScenarioImpact = {
    id: "single-name",
    name: top
      ? `${top.ticker} −${Math.round(sev.topNameDrop * 100)}%`
      : "Single-name crash",
    summary: top
      ? `Largest book weight (${pct(top.sharePct)} of cost basis) gaps down.`
      : "No concentrated stock lot to stress.",
    tone: topTone,
    figures: [
      { label: "Name MTM", value: top ? `−${money(topLoss)}` : "—", tone: topTone },
      {
        label: "Basis in name",
        value: top ? money(top.costBasis) : "—",
        tone: "neutral",
      },
      {
        label: "Weight",
        value: top ? pct(top.sharePct) : "—",
        tone: topTone,
      },
    ],
    consequences: top
      ? [
          `${top.ticker} mark loss ~${money(topLoss)} on ${top.quantity.toLocaleString("en-US")} shares.`,
          top.sharePct >= 30
            ? "Concentration is high enough that one ticker can move the whole margin picture."
            : "Weight is moderate; the rest of the book still diversifies some of the hit.",
          puts.some((p) => p.ticker === top.ticker)
            ? `Open short puts on ${top.ticker} get much closer to (or deeper) ITM — assignment risk rises with the gap.`
            : `No open short puts on ${top.ticker} in this book.`,
        ]
      : ["Add stock lots to see single-name stress."],
  };

  const baseMarginMo = monthlyMarginCost ?? marginUsed * (apr / 12);
  const shockedMarginMo = baseMarginMo * sev.marginRateMult;
  const rateTone: PfolioScenarioImpact["tone"] =
    shockedMarginMo > (cashEquivalent ?? 0) / 3
      ? "risk"
      : shockedMarginMo > (cashEquivalent ?? 0) / 8
        ? "watch"
        : "good";
  const rateScenario: PfolioScenarioImpact = {
    id: "margin-rate",
    name: `Margin rate ×${sev.marginRateMult}`,
    summary: `Monthly interest estimate scales from ${money(baseMarginMo)} to ${money(shockedMarginMo)}.`,
    tone: rateTone,
    figures: [
      { label: "Monthly now", value: money(baseMarginMo), tone: "neutral" },
      { label: "Monthly stressed", value: money(shockedMarginMo), tone: rateTone },
      {
        label: "Runway after",
        value:
          cashEquivalent != null && cashEquivalent > 0 && shockedMarginMo > 0
            ? `${(cashEquivalent / shockedMarginMo).toFixed(1)} mo`
            : "—",
        tone: rateTone,
      },
    ],
    consequences: [
      "Higher broker rates do not change marks, but they shrink the time you can sit in inventory.",
      shockedMarginMo > baseMarginMo * 2
        ? "At this severity, carrying the same margin used gets expensive quickly — trim basis or add capital."
        : "Interest drag rises, but not enough on its own to force an immediate de-risk in this sketch.",
      "Combine with an equity drop: utilization and the rate both move against you.",
    ],
  };

  return [equityScenario, assignScenario, buybackScenario, topScenario, rateScenario];
}

function avgPutStrike(puts: PfolioOption[]): number {
  let w = 0;
  let q = 0;
  for (const p of puts) {
    w += p.strike * p.contracts;
    q += p.contracts;
  }
  return q > 0 ? w / q : 0;
}

export function severityFromId(id: string | null | undefined): PfolioSeverity {
  const found = PFOLIO_SEVERITIES.find((s) => s.id === id);
  return found?.id ?? "moderate";
}
